import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import isEqual from 'lodash.isequal';
import {
  BaseTransaction,
  GeneralLedgerTransaction,
  IntegrationLog,
  Pagination,
  Response,
} from '@ui/src';
import { NostroTransactionValidator } from '../../types';
import { nostroTransactionsListApi } from '../services';
import { statusesTransactionsApi } from '../../../../store/statuses/services';
import { transactionApi } from '../../../transactions/store/services';

export interface NostroTransactionState {
  panelIsOpen: boolean;
  transaction?: BaseTransaction;
  transactionId?: string;
  validators?: NostroTransactionValidator;
  glTransactions?: GeneralLedgerTransaction[];
  integrationLogs?: IntegrationLog[];
  integrationLogsPagination?: Pagination;
}

const initialState: NostroTransactionState = {
  panelIsOpen: true,
  transaction: undefined,
  transactionId: undefined,
};

export const nostroTransactionSlice = createSlice({
  name: 'nostroTransaction',
  initialState,
  reducers: {
    changePanelState: (
      state,
      action: PayloadAction<{ state: boolean; transactionId?: string }>,
    ) => {
      state.panelIsOpen = action.payload.state;

      if (action.payload.state) {
        state.transactionId = action.payload.transactionId;
      } else {
        state.transactionId = undefined;
      }
    },
    clearNostroTransactionState: (state) => {
      state.transaction = undefined;
      state.transactionId = undefined;
      state.panelIsOpen = false;
      state.integrationLogs = undefined;
      state.integrationLogsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        nostroTransactionsListApi.endpoints.getNostroTransactionById
          .matchFulfilled,
        (state, action: PayloadAction<{ data: BaseTransaction }>) => {
          const prevTransaction = state.transaction;
          const nextTransaction = action.payload.data;

          if (!prevTransaction) {
            state.transaction = nextTransaction;
          } else if (!isEqual(prevTransaction, nextTransaction)) {
            state.transaction = nextTransaction;
          }
        },
      )
      .addMatcher(
        nostroTransactionsListApi.endpoints.getNostroTransactionValidators
          .matchFulfilled,
        (
          state,
          action: PayloadAction<{ data: NostroTransactionValidator }>,
        ) => {
          state.validators = action.payload.data;
        },
      );
    builder.addMatcher(
      statusesTransactionsApi.endpoints.changeTransactionStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction }>) => {
        if (state.transaction) {
          const nextTransaction = { ...state.transaction };

          nextTransaction.baseellaStatus = action.payload.data.baseellaStatus;
          nextTransaction.latestQueuedStatuses = action.payload.data
            .latestQueuedStatuses
            ? action.payload.data.latestQueuedStatuses
            : nextTransaction.latestQueuedStatuses;

          state.transaction = nextTransaction;
        }
      },
    );
    builder.addMatcher(
      nostroTransactionsListApi.endpoints.updateNostroTransaction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction }>) => {
        state.transaction = {
          ...state.transaction,
          ...action.payload.data,
        };
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getGeneralLedgerTransactionValues.matchFulfilled,
      (state, action: PayloadAction<Response<GeneralLedgerTransaction>>) => {
        if (state.transaction) {
          state.glTransactions = action.payload.data.edges.map(
            (item) => item.node,
          );
        }
      },
    );
    builder.addMatcher(
      nostroTransactionsListApi.endpoints.getNostroIntegrationLogs
        .matchFulfilled,
      (state, action: PayloadAction<Response<IntegrationLog>>) => {
        if (state.transaction) {
          state.integrationLogs = action.payload.data.edges.map(
            (item) => item.node,
          );

          state.integrationLogsPagination = action.payload.data.pageInfo;
        }
      },
    );
  },
});

export const { changePanelState, clearNostroTransactionState } =
  nostroTransactionSlice.actions;

export default nostroTransactionSlice.reducer;
